"use client"

import React from 'react';
import { motion } from 'framer-motion';
import Link from 'next/link';
import Image from 'next/image';
import { useTheme } from '../../../components/layout/ThemeProvider';
import ContactForm from '../../../components/ui/ContactForm';

const ManutencaoEletronicosPage = () => {
  const { theme } = useTheme();
  
  // Animation variants
  const fadeIn = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { duration: 0.6 } 
    }
  };

  // Categorias de dispositivos - versão compacta
  const categorias = [
    {
      id: 'tvs',
      title: 'TVs e Monitores',
      description: 'Reparo avançado de displays',
      dispositivos: [
        'Smart TVs', 'TVs LED/OLED/QLED', 'Monitores LCD/LED',
        'Monitores Gamer', 'Home Theaters', 'Projetores'
      ]
    },
    {
      id: 'impressoras',
      title: 'Impressoras',
      description: 'Manutenção e reparos técnicos',
      dispositivos: [
        'Impressoras Jato de Tinta', 'Impressoras Laser', 'Multifuncionais',
        'Plotters', 'Impressoras Térmicas', 'Scanners'
      ]
    },
    {
      id: 'energia',
      title: 'Equipamentos de Energia',
      description: 'Suporte a sistemas de energia',
      dispositivos: [
        'Nobreaks', 'Estabilizadores', 'Fontes de Alimentação',
        'Inversores', 'Filtros de Linha', 'UPS'
      ]
    },
    {
      id: 'outros',
      title: 'Outros Eletrônicos',
      description: 'Soluções para diversos dispositivos',
      dispositivos: [
        'Câmeras de Segurança', 'Soundbars', 'Equipamentos de Áudio',
        'Drones', 'Aparelhos de DVD/Blu-ray', 'Roteadores e Modems'
      ],
      observacao: "* Alguns reparos podem ser inviabilizados pelo custo das peças ou dificuldade em obter componentes específicos."
    }
  ];

  // Serviços principais - versão compacta
  const servicosPrincipais = [
    {
      titulo: 'Reparo de TVs e Monitores',
      descricao: 'Conserto de telas, placas e fonte de alimentação',
    },
    {
      titulo: 'Manutenção de Impressoras',
      descricao: 'Reparo de mecanismos e limpeza técnica',
    },
    {
      titulo: 'Conserto de Nobreaks',
      descricao: 'Reparo de circuitos e substituição de baterias',
    },
    {
      titulo: 'Manutenção de Home Theaters',
      descricao: 'Reparo de amplificadores e sistemas de áudio',
    },
    {
      titulo: 'Recuperação de Danos por Água',
      descricao: 'Tratamento para dispositivos com danos por líquidos',
    },
    {
      titulo: 'Substituição de Componentes',
      descricao: 'Troca de peças com componentes de qualidade',
    },
    {
      titulo: 'Atualização de Firmware',
      descricao: 'Instalação das versões mais recentes de software',
    }
  ];

  // Problemas comuns - versão compacta
  const problemasComuns = [
    {
      titulo: 'TVs e monitores com problemas',
      sintomas: 'Tela preta, imagem distorcida, falhas',
      solucao: 'Reparo na placa, substituição de componentes, reparo de backlight'
    },
    {
      titulo: 'Impressoras com falhas',
      sintomas: 'Papel atolado, manchas, ruídos, erros constantes',
      solucao: 'Limpeza técnica, substituição de peças, recalibração'
    },
    {
      titulo: 'Nobreaks com problemas',
      sintomas: 'Não segura carga, bateria viciada, alarme constante',
      solucao: 'Substituição de baterias, reparo de circuito, calibração'
    },
    {
      titulo: 'Problemas em equipamentos de áudio',
      sintomas: 'Sem som, distorção, ruídos, falhas intermitentes',
      solucao: 'Reparo de placas, substituição de componentes, recalibração'
    }
  ];

  // FAQ items - versão compacta
  const faqItems = [
    {
      pergunta: 'Quanto tempo leva para consertar meu equipamento eletrônico?',
      resposta: 'O prazo varia conforme o problema. Reparos simples: 1-5 dias úteis. Casos complexos: 5-10 dias úteis. Para equipamentos que precisam de peças importadas, o prazo pode se estender.'
    },
    {
      pergunta: 'Existe garantia para os serviços?',
      resposta: 'Sim. Oferecemos garantia de 90 dias para peças e serviços de hardware, e 30 dias para serviços de software e firmware.'
    },
    {
      pergunta: 'É possível recuperar um aparelho que sofreu danos por água?',
      resposta: 'Em muitos casos, sim. O sucesso depende do tempo entre o incidente e o atendimento, e do tipo de líquido. Recomendamos não ligar o dispositivo e trazê-lo o mais rápido possível.'
    },
    {
      pergunta: 'Vocês fornecem orçamento antes de realizar o reparo?',
      resposta: 'Sim, sempre. Só realizamos o serviço após aprovação do orçamento pelo cliente.'
    },
    {
      pergunta: 'Vale a pena consertar meu equipamento ou é melhor comprar um novo?',
      resposta: 'Isso depende de vários fatores: idade do aparelho, disponibilidade de peças, custo do reparo versus valor de um novo, e importância dos dados contidos no dispositivo. Fazemos uma avaliação honesta e transparente.'
    }
  ];

  // Benefícios - versão compacta
  const nossosDestaques = [
    {
      titulo: 'Diagnóstico Gratuito',
      descricao: 'Avaliação completa sem compromisso'
    },
    {
      titulo: 'Técnicos Especializados',
      descricao: 'Equipe com formação e certificações'
    },
    {
      titulo: 'Componentes de Qualidade',
      descricao: 'Peças originais ou homologadas'
    },
    {
      titulo: 'Garantia nos Serviços',
      descricao: 'Segurança e tranquilidade'
    },
    {
      titulo: 'Atendimento Transparente',
      descricao: 'Orçamentos claros e sem surpresas'
    }
  ];

  return (
    <>
      {/* Breadcrumbs */}
      <div className={`${theme === 'dark' ? 'bg-gray-800' : 'bg-gray-100'} py-2`}>
        <div className="container mx-auto px-4">
          <nav className="flex" aria-label="Breadcrumb">
            <ol className="flex items-center space-x-2">
              <li><Link href="/" className={`${theme === 'dark' ? 'text-gray-300 hover:text-white' : 'text-gray-600 hover:text-gray-900'}`}>Início</Link></li>
              <li className={`${theme === 'dark' ? 'text-gray-500' : 'text-gray-500'}`}>
                <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" /></svg>
              </li>
              <li><Link href="/servicos" className={`${theme === 'dark' ? 'text-gray-300 hover:text-white' : 'text-gray-600 hover:text-gray-900'}`}>Serviços</Link></li>
              <li className={`${theme === 'dark' ? 'text-gray-500' : 'text-gray-500'}`}>
                <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" /></svg>
              </li>
              <li><span className={`${theme === 'dark' ? 'text-gray-100' : 'text-gray-900'} font-medium`}>Manutenção de Eletrônicos</span></li>
            </ol>
          </nav>
        </div>
      </div>

      {/* Hero Section */}
      <section className={`py-20 ${theme === 'dark' ? 'bg-gray-900' : 'bg-white'} relative overflow-hidden`}>
        <div className="container mx-auto px-4 relative z-10">
          <div className="flex flex-col lg:flex-row items-center gap-12">
            <motion.div initial="hidden" animate="visible" variants={fadeIn} className="lg:w-1/2">
              <div className={`inline-block px-3 py-1 text-sm font-semibold ${theme === 'dark' ? 'text-blue-300 bg-blue-900/50' : 'text-blue-800 bg-blue-100'} rounded-full mb-3`}>
                Assistência Técnica Especializada
              </div>
              <h1 className={`text-4xl lg:text-5xl font-bold leading-tight ${theme === 'dark' ? 'text-white' : 'text-gray-900'} mb-6`}>
                <span className="text-blue-600">Reparo</span> e Manutenção de Eletrônicos
              </h1>
              <p className={`text-xl ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'} mb-8`}>
                Serviço especializado para TVs, monitores, impressoras e outros equipamentos eletrônicos. 
                Soluções eficientes para prolongar a vida útil dos seus aparelhos.
              </p>

              <div className="flex flex-col sm:flex-row gap-4">
                <motion.a href="#contato" whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}
                  className="px-8 py-3 bg-blue-600 hover:bg-blue-700 text-white font-extrabold text-lg rounded-lg shadow-lg hover:shadow-xl transition-all text-center"
                  style={{
                    textShadow: '0 1px 2px rgba(0,0,0,0.2)',
                    color: '#ffffff'
                  }}
                >
                  Solicitar Orçamento
                </motion.a>
                <motion.a href="#servicos" whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}
                  className={`px-8 py-3 ${theme === 'dark' ? 'bg-gray-800 hover:bg-gray-700 text-white' : 'bg-gray-100 hover:bg-gray-200 text-gray-800'} font-extrabold text-lg rounded-lg shadow hover:shadow-lg transition-all text-center`}>
                  Ver Serviços
                </motion.a>
              </div>
            </motion.div>
            
            <motion.div initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.6, delay: 0.3 }} className="lg:w-1/2">
              <div className="relative">
                <div className={`w-full h-96 rounded-lg ${theme === 'dark' ? 'bg-gray-800' : 'bg-blue-50'} flex items-center justify-center overflow-hidden`}>
                  <Image src="/images/manutencao-eletronicos-illustration.webp" alt="Manutenção de Eletrônicos" width={500} height={400} className="object-contain max-w-full max-h-full" />
                </div>
                
                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.8, duration: 0.5 }}
                  className={`absolute -bottom-6 -right-6 p-6 rounded-lg shadow-xl ${theme === 'dark' ? 'bg-gray-800 border border-gray-700' : 'bg-white'}`}>
                  <p className="text-blue-600 font-bold text-4xl">85%</p>
                  <p className={`${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'} text-sm`}>
                    taxa de sucesso
                  </p>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Categorias Section */}
      <section id="categorias" className={`py-16 ${theme === 'dark' ? 'bg-gray-800' : 'bg-gray-50'}`}>
        <div className="container mx-auto px-4">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <h2 className={`text-3xl md:text-4xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'} mb-4`}>
              Equipamentos que Consertamos
            </h2>
            <p className={`text-lg ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
              Oferecemos soluções para uma ampla variedade de dispositivos eletrônicos, desde televisores até equipamentos de energia.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {categorias.map((categoria) => (
              <motion.div 
                key={categoria.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                viewport={{ once: true }}
                className={`p-6 rounded-xl ${theme === 'dark' ? 'bg-gray-700 hover:bg-gray-750' : 'bg-white hover:bg-blue-50'} shadow-lg hover:shadow-xl transition-all`}
              >
                <h3 className={`text-xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'} mb-2`}>{categoria.title}</h3>
                <p className={`${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'} mb-4`}>{categoria.description}</p>
                
                <ul className={`mt-4 space-y-1 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
                  {categoria.dispositivos.map((dispositivo, index) => (
                    <li key={index} className="flex items-center">
                      <svg className="w-4 h-4 mr-2 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      {dispositivo}
                    </li>
                  ))}
                </ul>
                
                {categoria.observacao && (
                  <p className={`mt-4 text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                    {categoria.observacao}
                  </p>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Serviços Section */}
      <section id="servicos" className={`py-16 ${theme === 'dark' ? 'bg-gray-900' : 'bg-white'}`}>
        <div className="container mx-auto px-4">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <h2 className={`text-3xl md:text-4xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'} mb-4`}>
              Nossos Serviços
            </h2>
            <p className={`text-lg ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
              Oferecemos uma ampla gama de soluções para problemas em equipamentos eletrônicos.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {servicosPrincipais.map((servico, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className={`p-6 rounded-xl ${theme === 'dark' ? 'bg-gray-800 hover:bg-gray-750' : 'bg-gray-50 hover:bg-blue-50'} shadow hover:shadow-md transition-all`}
              >
                <h3 className={`text-xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'} mb-2`}>
                  {servico.titulo}
                </h3>
                <p className={`${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
                  {servico.descricao}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Problemas Comuns Section */}
      <section className={`py-16 ${theme === 'dark' ? 'bg-gray-800' : 'bg-gray-50'}`}>
        <div className="container mx-auto px-4">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <h2 className={`text-3xl md:text-4xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'} mb-4`}>
              Problemas Comuns
            </h2>
            <p className={`text-lg ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
              Identificamos e resolvemos os problemas mais frequentes em equipamentos eletrônicos.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-6">
            {problemasComuns.map((problema, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                viewport={{ once: true }}
                className={`p-6 rounded-xl ${theme === 'dark' ? 'bg-gray-700' : 'bg-white'} shadow-lg`}
              >
                <h3 className={`text-xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'} mb-3`}>
                  {problema.titulo}
                </h3>
                <div className="mb-4">
                  <p className={`font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>Sintomas comuns:</p>
                  <p className={`${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>{problema.sintomas}</p>
                </div>
                <div className={`pt-4 border-t ${theme === 'dark' ? 'border-gray-600' : 'border-gray-200'}`}>
                  <p className={`font-medium ${theme === 'dark' ? 'text-blue-400' : 'text-blue-600'}`}>Nossa solução:</p>
                  <p className={`${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>{problema.solucao}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className={`py-16 ${theme === 'dark' ? 'bg-gray-900' : 'bg-white'}`}>
        <div className="container mx-auto px-4">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <h2 className={`text-3xl md:text-4xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'} mb-4`}>
              Perguntas Frequentes
            </h2>
            <p className={`text-lg ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
              Dúvidas comuns sobre nossos serviços de manutenção de eletrônicos.
            </p>
          </div>
          
          <div className="max-w-4xl mx-auto"
               itemScope
               itemType="https://schema.org/FAQPage">
            {faqItems.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                viewport={{ once: true }}
                className={`mb-6 rounded-xl ${
                  theme === 'dark' ? 'bg-gray-800 border border-gray-700' : 'bg-gray-50 border border-gray-200'
                } shadow-md overflow-hidden`}
                itemScope
                itemType="https://schema.org/Question"
              >
                <details className="group">
                  <summary className={`flex items-center justify-between p-6 cursor-pointer ${
                    theme === 'dark' ? 'text-white' : 'text-gray-900'
                  }`}>
                    <h3 className="text-lg font-semibold" itemProp="name">{item.pergunta}</h3>
                    <span className="relative ml-2 flex-shrink-0">
                      <svg 
                        className={`w-5 h-5 transition-transform transform ${
                          theme === 'dark' ? 'text-blue-300' : 'text-blue-600'
                        } group-open:rotate-180`} 
                        fill="none" 
                        stroke="currentColor" 
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
                      </svg>
                    </span>
                  </summary>
                  <div 
                    className={`px-6 pb-6 pt-0 ${
                      theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                    }`}
                    itemScope
                    itemProp="acceptedAnswer" 
                    itemType="https://schema.org/Answer"
                  >
                    <p itemProp="text">{item.resposta}</p>
                  </div>
                </details>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Destaques Section */}
      <section className={`py-16 ${theme === 'dark' ? 'bg-gray-800' : 'bg-gray-50'}`}>
        <div className="container mx-auto px-4">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <h2 className={`text-3xl md:text-4xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'} mb-4`}>
              Por Que Escolher Nossos Serviços
            </h2>
            <p className={`text-lg ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
              Diferenciais que fazem nossa assistência técnica se destacar.
            </p>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6">
            {nossosDestaques.map((destaque, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.4 }}
                viewport={{ once: true }}
                className={`p-6 rounded-xl text-center ${theme === 'dark' ? 'bg-gray-700' : 'bg-white'} shadow-lg`}
              >
                <h3 className={`text-lg font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'} mb-2`}>
                  {destaque.titulo}
                </h3>
                <p className={`${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
                  {destaque.descricao}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA + Contact Form */}
      <section id="contato" className={`py-16 ${theme === 'dark' ? 'bg-gray-900' : 'bg-white'} relative overflow-hidden`}>
        {/* Background decorativo */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <svg 
            className="absolute top-0 left-0 transform -translate-x-1/2 -translate-y-1/2" 
            width="600" 
            height="600" 
            fill="none" 
            viewBox="0 0 600 600"
            aria-hidden="true"
          >
            <circle 
              cx="300" 
              cy="300" 
              r="300" 
              fill={theme === 'dark' ? "#1E40AF" : "#3B82F6"} 
              fillOpacity="0.05" 
            />
          </svg>
          
          <svg 
            className="absolute bottom-0 right-0 transform translate-x-1/2 translate-y-1/2" 
            width="600" 
            height="600" 
            fill="none" 
            viewBox="0 0 600 600"
            aria-hidden="true"
          >
            <circle 
              cx="300" 
              cy="300" 
              r="300" 
              fill={theme === 'dark' ? "#4F46E5" : "#818CF8"} 
              fillOpacity="0.05" 
            />
          </svg>
        </div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <div className={`inline-block px-3 py-1 text-sm font-semibold ${
                theme === 'dark' 
                  ? 'text-blue-300 bg-blue-900/50' 
                  : 'text-blue-800 bg-blue-100'
                } rounded-full mb-3`}>
                Vamos Conversar
              </div>
              <h2 className={`text-3xl md:text-4xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'} mb-6`}>
                Entre em Contato
              </h2>
              <p className={`text-lg ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'} mb-8`}>
                Entre em contato para um diagnóstico gratuito. Nossa equipe técnica está pronta para 
                resolver o problema do seu equipamento com rapidez e eficiência.
              </p>
              
              <div className="space-y-6">
                <div className={`flex items-center ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                  <div className={`w-12 h-12 rounded-full ${theme === 'dark' ? 'bg-blue-900/40' : 'bg-blue-100'} 
                  flex items-center justify-center mr-4 ${theme === 'dark' ? 'text-blue-300' : 'text-blue-700'}`}>
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                    </svg>
                  </div>
                  <div>
                    <p className="font-medium">Telefone</p>
                    <a href="tel:+551932060706" className={`${theme === 'dark' ? 'text-blue-300 hover:text-blue-200' : 'text-blue-600 hover:text-blue-800'}`}>
                      (19) 3206-0706
                    </a>
                  </div>
                </div>

                <div className={`flex items-center ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                  <div className={`w-12 h-12 rounded-full ${theme === 'dark' ? 'bg-green-900/40' : 'bg-green-100'} 
                  flex items-center justify-center mr-4 ${theme === 'dark' ? 'text-green-300' : 'text-green-700'}`}>
                    <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                    </svg>
                  </div>
                  <div>
                    <p className="font-medium">WhatsApp</p>
                    <a href="https://wa.me/5519994799400" target="_blank" rel="noopener noreferrer" 
                    className={`${theme === 'dark' ? 'text-green-300 hover:text-green-200' : 'text-green-600 hover:text-green-800'}`}>
                      (19) 99479-9400
                    </a>
                  </div>
                </div>
              
                <div className={`flex items-center ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                  <div className={`w-12 h-12 rounded-full ${theme === 'dark' ? 'bg-blue-900/40' : 'bg-blue-100'} 
                  flex items-center justify-center mr-4 ${theme === 'dark' ? 'text-blue-300' : 'text-blue-700'}`}>
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <div>
                    <p className="font-medium">Email</p>
                    <a href="mailto:contato@webimports.com.br" className={`${theme === 'dark' ? 'text-blue-300 hover:text-blue-200' : 'text-blue-600 hover:text-blue-800'}`}>
                      contato@webimports.com.br
                    </a>
                  </div>
                </div>
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className={`${theme === 'dark' ? 'bg-gray-800 border border-gray-700' : 'bg-white shadow-xl'} rounded-xl p-6`}
            >
              <h3 className={`text-xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'} mb-6`}>
                Solicite um diagnóstico gratuito
              </h3>
              
              <ContactForm />
              
              <p className={`mt-4 text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                Ao enviar este formulário, você concorda com nossa política de privacidade.
                Seus dados não serão compartilhados com terceiros.
              </p>
            </motion.div>
          </div>
        </div>
      </section>
    </>
  );
};

export default ManutencaoEletronicosPage;